import React, { useEffect, useRef, useState, useCallback } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './VoiceGravity.css';

const PHRASE = 'WHISPER · LIFTS · SHOUT · FALLS · BREATHE · TO · WAKE';

// Audio thresholds (normalized 0..1 after dB mapping)
const WAKE_THRESHOLD = 0.35;       // loudness that "wakes" settled particles
const SETTLE_VELOCITY = 0.4;       // |vy| under which a floored particle locks

// Gravity range — negative = float up (silence), positive = fall (loud)
const G_QUIET = -0.08;
const G_LOUD  = 1.4;

// Physics
const FRICTION = 0.985;
const BOUNCE = 0.35;
const FLOOR_PAD = 36;             // px from canvas bottom
const SIDE_PAD = 16;

interface Particle {
  ch: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  fontSize: number;
  settled: boolean;
}

type PermState = 'idle' | 'requesting' | 'granted' | 'denied';

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

const VoiceGravity: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animRef = useRef<number>(0);

  // Audio
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioBufRef = useRef<Uint8Array | null>(null);

  // Smoothed loudness 0..1
  const loudnessRef = useRef(0);
  const peakRef = useRef(0);
  const dbRef = useRef(-60);
  const gravityRef = useRef(0);
  const wokeRef = useRef(false);

  const [perm, setPerm] = useState<PermState>('idle');
  const [woke, setWoke] = useState(false);
  const [hudDb, setHudDb] = useState(-60);
  const [hudGravity, setHudGravity] = useState(0);
  const [hudLevel, setHudLevel] = useState(0);
  const hudFrameRef = useRef(0);

  const exp = experiments.find(e => e.id === 'voice-gravity');

  // ── Init particles for the current viewport ──────────
  const initParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.width / window.devicePixelRatio;
    const h = canvas.height / window.devicePixelRatio;

    const chars = PHRASE.split('');
    const fontSize = clamp(Math.min(w, h) * 0.06, 18, 56);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = `${fontSize}px 'VT323', monospace`;

    const particles: Particle[] = [];
    const startY = h * 0.4;
    let cursorX = SIDE_PAD;
    const lineGap = fontSize * 1.4;
    let line = 0;

    for (const ch of chars) {
      const m = ctx.measureText(ch);
      const cw = m.width;
      if (cursorX + cw > w - SIDE_PAD) {
        cursorX = SIDE_PAD;
        line++;
      }
      const px = cursorX;
      const py = startY + line * lineGap;
      particles.push({
        ch,
        x: px,
        y: py,
        vx: (Math.random() - 0.5) * 0.4,
        vy: 0,
        w: cw,
        h: fontSize,
        fontSize,
        settled: false,
      });
      cursorX += cw;
    }

    particlesRef.current = particles;
  }, []);

  // ── Resize handler ───────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext('2d');
      ctx?.scale(dpr, dpr);
      initParticles();
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [initParticles]);

  // ── Audio setup on user gesture ──────────────────────
  const enableMic = useCallback(async () => {
    if (perm === 'requesting' || perm === 'granted') return;
    setPerm('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      streamRef.current = stream;
      audioBufRef.current = new Uint8Array(analyser.fftSize);
      setPerm('granted');
    } catch (err) {
      console.warn('[voice-gravity] mic denied:', err);
      setPerm('denied');
    }
  }, [perm]);

  // ── Animation loop ───────────────────────────────────
  useEffect(() => {
    if (perm !== 'granted') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const readLoudness = (): number => {
      const analyser = analyserRef.current;
      const buf = audioBufRef.current;
      if (!analyser || !buf) return 0;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const db = 20 * Math.log10(rms || 1e-9);
      // Map -60 (silence) .. -10 (loud) → 0..1
      const t = clamp((db + 60) / 50, 0, 1);
      dbRef.current = db;
      return t;
    };

    const tick = () => {
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      const floor = h - FLOOR_PAD;

      // Smooth loudness (low-pass to remove jitter)
      const raw = readLoudness();
      loudnessRef.current = lerp(loudnessRef.current, raw, 0.25);
      const lv = loudnessRef.current;

      // Track instantaneous peak for wake-trigger
      const spike = raw - peakRef.current;
      peakRef.current = Math.max(raw, peakRef.current * 0.9);

      // Wake on first significant sound
      if (!wokeRef.current && raw > 0.18) {
        wokeRef.current = true;
        setWoke(true); // trigger one re-render to hide hint
      }

      // Gravity from loudness
      const gravity = lerp(G_QUIET, G_LOUD, lv);
      gravityRef.current = gravity;

      // HUD: throttle React updates to ~10fps (every 6 frames)
      hudFrameRef.current++;
      if (hudFrameRef.current % 6 === 0) {
        setHudDb(dbRef.current);
        setHudLevel(lv);
        setHudGravity(gravity);
      }

      // Wake-kick: a loud spike unsettles everything
      if (raw > WAKE_THRESHOLD && spike > 0.12) {
        for (const p of particlesRef.current) {
          if (p.settled) {
            p.settled = false;
            p.vy = -(2 + Math.random() * 4) * (raw * 1.5);
            p.vx += (Math.random() - 0.5) * 3;
          }
        }
      }

      // Clear with subtle trail
      ctx.fillStyle = 'rgba(10, 10, 10, 0.35)';
      ctx.fillRect(0, 0, w, h);

      // Update + draw particles
      for (const p of particlesRef.current) {
        if (!p.settled) {
          p.vy += gravity;
          p.vx *= FRICTION;
          p.vy *= FRICTION;
          p.x += p.vx;
          p.y += p.vy;

          // Side walls
          if (p.x < SIDE_PAD) { p.x = SIDE_PAD; p.vx *= -BOUNCE; }
          if (p.x + p.w > w - SIDE_PAD) { p.x = w - SIDE_PAD - p.w; p.vx *= -BOUNCE; }

          // Ceiling (only when negative gravity pushes up)
          if (p.y < SIDE_PAD) { p.y = SIDE_PAD; p.vy *= -BOUNCE; }

          // Floor
          if (p.y > floor - p.h) {
            p.y = floor - p.h;
            if (Math.abs(p.vy) < SETTLE_VELOCITY && gravity > 0) {
              p.vy = 0;
              p.vx = 0;
              p.settled = true;
            } else {
              p.vy *= -BOUNCE;
              p.vx *= 0.7;
            }
          }
        }

        // Draw
        ctx.font = `${p.fontSize}px 'VT323', monospace`;
        ctx.textBaseline = 'top';
        const alpha = wokeRef.current ? (p.settled ? 0.65 : 0.9) : 0.18;
        ctx.fillStyle = `rgba(0, 255, 153, ${alpha})`;
        ctx.fillText(p.ch, p.x, p.y);
      }

      animRef.current = requestAnimationFrame(tick);
    };

    tick();
    return () => cancelAnimationFrame(animRef.current);
  }, [perm]);

  // ── Cleanup audio on unmount ─────────────────────────
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioCtxRef.current?.close();
    };
  }, []);

  // ── Render ───────────────────────────────────────────
  return (
    <div className="voiceGravity">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />
      <canvas ref={canvasRef} className="voiceCanvas" />

      {perm !== 'granted' && (
        <div className="voicePermOverlay" onClick={enableMic}>
          <div className="voicePermBox">
            <div className="voicePermIcon">♪</div>
            <div className="voicePermTitle">tap to enable mic</div>
            <div className="voicePermDesc">
              your voice becomes gravity<br />
              whisper to lift · shout to drop
            </div>
            {perm === 'denied' && (
              <div className="voicePermError">[ permission denied — reload to retry ]</div>
            )}
          </div>
        </div>
      )}

      {perm === 'granted' && !woke && (
        <div className="voiceHint">make a sound to wake the page</div>
      )}

      {perm === 'granted' && (
        <div className="voiceHud">
          <span>dB <span className="hudValue">{hudDb.toFixed(0)}</span></span>
          <span>
            level
            <span className="voiceBar" style={{ marginLeft: 6 }}>
              <span
                className="voiceBarFill"
                style={{ transform: `scaleX(${hudLevel.toFixed(2)})` }}
              />
            </span>
          </span>
          <span>g <span className="hudValue">{hudGravity.toFixed(2)}</span></span>
        </div>
      )}
    </div>
  );
};

export default VoiceGravity;
